import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import isEqual from 'lodash/isEqual';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PlusCircleIcon from '../../resources/svg/add.svg';
import { AutoCompleteField, Button, Checkbox, Dropdown, TextField } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import RuleEditor from './RuleEditor';
import ResultRow from './ResultRow';
import CohortPeriodsEditor from './CohortPeriodsEditor';
import { filterByLabelOnly } from './selectFilter';
import { CustomMetricsContext } from '../context';
import {
    CELL_FORMAT_CUSTOM,
    CHOOSE_FORMAT_PLACEHOLDER,
    COHORT_METRIC_TYPE,
    DEFAULT_PERIOD,
    METRIC_NAME_MAX_LENGTH,
    TOOLTIP_MAX_LENGTH,
    TRANSLATION_PREFIX,
    getDefaultResult,
    getDefaultRule,
} from '../consts';
import {
    generateId,
    getFormatJSON,
    isNewMetric,
    normalizeName,
    refTableHasEventColumn,
    validateCustomMetric,
} from '../utils';
import css from '../page.css';

const deepCopy = value => JSON.parse(JSON.stringify(value));

function CustomMetricForm({ metric: initialMetric, onSave, onCancel, onDelete = null, saving = false, translate }) {
    const context = useContext(CustomMetricsContext);
    const { allMetrics, visibleMetrics, displayFormatsList } = context;

    const [metric, setMetric] = useState(() => deepCopy(initialMetric));
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState(null);
    // Remount the display-name autocomplete after a selection so react-select displays the
    // picked metric; typing must not remount (it would lose focus).
    const [nameSelectionCount, setNameSelectionCount] = useState(0);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const { errors, isValid } = useMemo(() => validateCustomMetric(metric), [metric]);

    const isNew = isNewMetric(initialMetric);

    const setFields = fields => setMetric(currentMetric => ({ ...currentMetric, ...fields }));

    const applyDisplayFormat = (currentMetric, fieldType) => {
        const updatedMetric = { ...currentMetric, field_type: fieldType };
        updatedMetric.json = getFormatJSON(updatedMetric);
        if (fieldType?.key !== CELL_FORMAT_CUSTOM) {
            updatedMetric.custom_display_format = JSON.parse(updatedMetric.json || '{}');
        }
        return updatedMetric;
    };

    const onDisplayNameTyped = text => {
        // Cap the display name like the legacy `ng-maxlength="40"` and the sibling name field, so it
        // can't exceed the limit validateCustomMetric enforces.
        const displayName = text.slice(0, METRIC_NAME_MAX_LENGTH);
        setMetric(currentMetric => ({
            ...currentMetric,
            display_name: displayName,
            ...(isNew ? { name: normalizeName(displayName) } : {}),
        }));
    };

    // Mirrors the legacy onNameSelect: picking an existing metric copies its name, display name
    // and (when recognized) its display format.
    const onDisplayNameSelected = option => {
        const existingMetric = allMetrics.find(({ name }) => name === option.value);
        if (!existingMetric) {
            return;
        }

        setNameSelectionCount(count => count + 1);
        setMetric(currentMetric => {
            let updatedMetric = {
                ...currentMetric,
                name: existingMetric.name,
                display_name: existingMetric.display_name,
            };

            if (existingMetric.display_format) {
                const matchingFormat = displayFormatsList.find(({ key, ...format }) =>
                    Object.entries(existingMetric.display_format).every(([formatKey, formatValue]) =>
                        isEqual(format[formatKey], formatValue)
                    )
                );

                if (matchingFormat) {
                    updatedMetric = applyDisplayFormat(updatedMetric, matchingFormat);
                }
            }

            return updatedMetric;
        });
    };

    const onDisplayFormatSelected = formatItem => {
        const fieldType = displayFormatsList.find(({ key }) => key === formatItem.name);
        setMetric(currentMetric => applyDisplayFormat(currentMetric, fieldType));
    };

    // Ports the legacy removeMetricPeriod/addMetricPeriod bookkeeping around result changes.
    const countMetricAppearances = (currentMetric, fieldName) => {
        let count = currentMetric.rules.reduce(
            (total, rule) =>
                total + (rule.result.metric?.name === fieldName || rule.result.value?.name === fieldName ? 1 : 0),
            0
        );

        if (currentMetric.default_result?.metric?.name === fieldName) {
            count += 1;
        }

        return count;
    };

    const removeMetricPeriod = (currentMetric, periods, field) => {
        if (!field || typeof field !== 'object' || !field.name) {
            return periods;
        }

        const appearances = countMetricAppearances(currentMetric, field.name);
        if (periods[field.name] && appearances - 1 <= 0) {
            const { [field.name]: removed, ...remainingPeriods } = periods;
            return remainingPeriods;
        }

        return periods;
    };

    const addMetricPeriod = (periods, field) => {
        if (field && typeof field === 'object' && field.metric_type === COHORT_METRIC_TYPE && !periods[field.name]) {
            return { ...periods, [field.name]: { ...DEFAULT_PERIOD } };
        }

        return periods;
    };

    const updatePeriodsForResultChange = (currentMetric, newResult, oldResult) => {
        let periods = { ...currentMetric.periods };

        if (newResult.metric !== oldResult.metric) {
            periods = removeMetricPeriod(currentMetric, periods, oldResult.metric);
            periods = addMetricPeriod(periods, newResult.metric);
        }

        if (newResult.value !== oldResult.value) {
            periods = removeMetricPeriod(currentMetric, periods, oldResult.value);
            periods = addMetricPeriod(periods, newResult.value);
        }

        return periods;
    };

    const onRuleResultChange = (ruleIndex, newResult, oldResult) => {
        setMetric(currentMetric => ({
            ...currentMetric,
            periods: updatePeriodsForResultChange(currentMetric, newResult, oldResult),
            rules: currentMetric.rules.map((rule, index) =>
                index === ruleIndex ? { ...rule, result: newResult } : rule
            ),
        }));
    };

    const onRuleSumModeChange = (ruleIndex, isCpeSum) => {
        // One setMetric, not onRuleChange followed by onRuleResultChange: the second would rebuild the rule
        // from the stale `rule` prop and discard the result update.
        setMetric(currentMetric => {
            const currentRule = currentMetric.rules[ruleIndex];
            const { _preSum: preSum, ...result } = currentRule.result;
            const defaults = getDefaultResult();

            // The operand and operator are inert in sum mode, so clear them rather than leave a stale
            // metric the UI no longer shows -- but remember them, because switching back must not strand
            // the rule with a blank operand and therefore a disabled operator and reference table.
            // Mirrors the _previous stash ResultRow uses for its operator/value toggle.
            let newResult;
            if (isCpeSum) {
                // Sum mode only offers reference tables that declare an event column, so anything else -- a
                // metric, a typed number, or a table whose rows cannot name an event -- is dropped rather
                // than left showing in a field that no longer lists it.
                const keepsValue = result.isRefTable && refTableHasEventColumn(result.value);
                newResult = {
                    ...result,
                    _preSum: { metric: result.metric, operator: result.operator },
                    metric: defaults.metric,
                    operator: defaults.operator,
                    value: keepsValue ? result.value : defaults.value,
                    isRefTable: keepsValue,
                    showOpValue: true,
                };
            } else {
                // Leaving sum mode keeps the reference table -- only the operand and operator change back.
                // A rule stored as sum-mode has nothing stashed and keeps a blank operand, which the
                // existing required-field error prompts for.
                newResult = {
                    ...result,
                    metric: preSum?.metric || defaults.metric,
                    operator: preSum?.operator || defaults.operator,
                    showOpValue: true,
                };
            }

            return {
                ...currentMetric,
                periods: updatePeriodsForResultChange(currentMetric, newResult, currentRule.result),
                rules: currentMetric.rules.map((rule, index) =>
                    index === ruleIndex ? { ...rule, isCpeSum, result: newResult } : rule
                ),
            };
        });
    };

    const onDefaultResultChange = (newResult, oldResult) => {
        setMetric(currentMetric => ({
            ...currentMetric,
            periods: updatePeriodsForResultChange(currentMetric, newResult, oldResult),
            default_result: newResult,
        }));
    };

    const onRuleChange = (ruleIndex, newRule) => {
        setMetric(currentMetric => ({
            ...currentMetric,
            rules: currentMetric.rules.map((rule, index) => (index === ruleIndex ? newRule : rule)),
        }));
    };

    const onRuleRemove = ruleIndex => {
        setMetric(currentMetric => {
            const rule = currentMetric.rules[ruleIndex];
            let periods = { ...currentMetric.periods };
            periods = removeMetricPeriod(currentMetric, periods, rule.result.metric);
            periods = removeMetricPeriod(currentMetric, periods, rule.result.value);

            return {
                ...currentMetric,
                periods,
                rules: currentMetric.rules.filter((_, index) => index !== ruleIndex),
            };
        });
    };

    const onAddRule = () => {
        setMetric(currentMetric => ({
            ...currentMetric,
            rules: [...currentMetric.rules, { id: generateId(), ...getDefaultRule() }],
        }));
    };

    const onRuleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) {
            return;
        }

        setMetric(currentMetric => {
            const oldIndex = currentMetric.rules.findIndex(({ id }) => id === active.id);
            const newIndex = currentMetric.rules.findIndex(({ id }) => id === over.id);
            return { ...currentMetric, rules: arrayMove(currentMetric.rules, oldIndex, newIndex) };
        });
    };

    const onPeriodChange = (metricName, period) => {
        setMetric(currentMetric => ({
            ...currentMetric,
            periods: { ...currentMetric.periods, [metricName]: period },
        }));
    };

    const onSubmit = async () => {
        setSubmitted(true);
        setServerError(null);

        if (!isValid) {
            return;
        }

        try {
            await onSave(metric);
        } catch (error) {
            setServerError(error?.text || error?.message || translate(`${TRANSLATION_PREFIX}.SAVE_ERROR`));
        }
    };

    const displayNameOptions = visibleMetrics.map(({ name, display_name: displayName }) => ({
        value: name,
        label: displayName,
    }));

    const displayFormatItems = displayFormatsList.map(({ key }) => ({ name: key, display_name: key }));
    const selectedDisplayFormat = metric.field_type
        ? { name: metric.field_type.key, display_name: metric.field_type.key }
        : { name: 'default', display_name: CHOOSE_FORMAT_PLACEHOLDER };

    const isCustomFormat = metric.field_type?.key === CELL_FORMAT_CUSTOM;

    return (
        <div className={css.metricForm}>
            {onDelete && (
                <button
                    type="button"
                    className={css.deleteMetricButton}
                    title={translate(`${TRANSLATION_PREFIX}.DELETE_CUSTOM_METRIC`)}
                    onClick={onDelete}
                >
                    <Icon name="delete-2-line" size={IconSizes.SMALL} variant={IconVariants.DARK} />
                </button>
            )}
            <div className={css.namesRow}>
                <span className={css.fieldLabel}>
                    <Translate id={`${TRANSLATION_PREFIX}.METRIC_DISPLAY_NAME`} />
                </span>
                <div className={css.nameField}>
                    <AutoCompleteField
                        key={`display-name-${nameSelectionCount}`}
                        isMulti={false}
                        searchable
                        controlled
                        options={displayNameOptions}
                        value={metric.display_name || ''}
                        placeholder={`${TRANSLATION_PREFIX}.PLACEHOLDERS.METRIC_NAME`}
                        onChange={onDisplayNameSelected}
                        onInputChange={onDisplayNameTyped}
                        debounceTime={0}
                        selectOptions={{ filterOption: filterByLabelOnly }}
                    />
                </div>
                <span className={css.fieldLabel}>
                    <Translate id={`${TRANSLATION_PREFIX}.METRIC_NAME`} />
                </span>
                <div className={css.nameField}>
                    <TextField
                        value={metric.name || ''}
                        placeholder={`${TRANSLATION_PREFIX}.PLACEHOLDERS.METRIC_NAME`}
                        maxLength={METRIC_NAME_MAX_LENGTH}
                        onChange={name => setFields({ name })}
                    />
                </div>
            </div>
            {submitted && errors.names && (
                <div className={css.errorText}>
                    <Translate id={`${TRANSLATION_PREFIX}.NAMES_REQUIRED`} />
                </div>
            )}
            <div className={css.checkboxesRow}>
                <Checkbox
                    label={translate(`${TRANSLATION_PREFIX}.METRIC_VISIBLE`)}
                    checked={!!metric.visible}
                    onChange={visible => setFields({ visible })}
                />
                <Checkbox
                    label={translate(`${TRANSLATION_PREFIX}.METRIC_DEFAULT`)}
                    checked={!!metric.is_default}
                    onChange={isDefault => setFields({ is_default: isDefault })}
                />
                <Checkbox
                    label={translate(`${TRANSLATION_PREFIX}.METRIC_RESTRICTED`)}
                    checked={!!metric.is_restricted}
                    onChange={isRestricted => setFields({ is_restricted: isRestricted })}
                />
                <Checkbox
                    label={translate(`${TRANSLATION_PREFIX}.METRIC_CUSTOM_EVENT`)}
                    checked={!!metric.should_appear_as_custom_event}
                    onChange={shouldAppear => setFields({ should_appear_as_custom_event: shouldAppear })}
                />
            </div>
            <div className={css.formatRow}>
                <span className={css.fieldLabel}>
                    <Translate id={`${TRANSLATION_PREFIX}.DISPLAY_FORMAT`} />:
                </span>
                <Dropdown
                    items={displayFormatItems}
                    selected={selectedDisplayFormat}
                    onSelection={onDisplayFormatSelected}
                    error={submitted && errors.fieldType ? `${TRANSLATION_PREFIX}.DISPLAY_FORMAT_REQUIRED` : null}
                    containerStyle={{ width: 340 }}
                    wrapperStyle={{ width: 340 }}
                />
                {submitted && errors.fieldType && (
                    <span className={css.errorText}>
                        <Translate id={`${TRANSLATION_PREFIX}.DISPLAY_FORMAT_REQUIRED`} />
                    </span>
                )}
            </div>
            <div className={css.formatRow}>
                <span className={css.fieldLabel}>
                    <Translate id={`${TRANSLATION_PREFIX}.FORMAT_JSON`} />:
                </span>
                <div className={css.jsonField}>
                    <TextField
                        value={metric.json || ''}
                        disabled={!isCustomFormat}
                        inputClassName={css.jsonInput}
                        onChange={json => setFields({ json })}
                    />
                </div>
                {submitted && errors.formatJson && (
                    <span className={css.errorText}>
                        <Translate id={`${TRANSLATION_PREFIX}.INVALID_FORMAT_JSON`} />
                    </span>
                )}
            </div>
            <div className={css.formatRow}>
                <span className={css.fieldLabel}>
                    <Translate id={`${TRANSLATION_PREFIX}.TOOLTIP`} />:
                </span>
                <div className={css.tooltipField}>
                    <TextField
                        value={metric.tooltip || ''}
                        maxLength={TOOLTIP_MAX_LENGTH}
                        onChange={tooltip => setFields({ tooltip })}
                    />
                </div>
            </div>
            <div className={css.rulesContainer}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRuleDragEnd}>
                    <SortableContext items={metric.rules.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
                        {metric.rules.map((rule, ruleIndex) => (
                            <RuleEditor
                                key={rule.id}
                                rule={rule}
                                onChange={newRule => onRuleChange(ruleIndex, newRule)}
                                onRemove={() => onRuleRemove(ruleIndex)}
                                onResultChange={(newResult, oldResult) =>
                                    onRuleResultChange(ruleIndex, newResult, oldResult)
                                }
                                onSumModeChange={isCpeSum => onRuleSumModeChange(ruleIndex, isCpeSum)}
                                errors={errors.rules[ruleIndex]}
                                showErrors={submitted}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
                <button type="button" className={css.addRuleButton} onClick={onAddRule}>
                    <PlusCircleIcon className={css.addRuleIcon} />
                    <Translate id={`${TRANSLATION_PREFIX}.ADD_RULE`} />
                </button>
                <h4 className={css.ruleSectionTitle}>
                    <Translate id={`${TRANSLATION_PREFIX}.IF_NO_VALUE_MATCHED_SET_DEFAULT_TO`} />
                </h4>
                <ResultRow
                    result={metric.default_result}
                    onChange={newResult => onDefaultResultChange(newResult, metric.default_result)}
                    errors={errors.defaultResult}
                    showErrors={submitted}
                />
                <CohortPeriodsEditor periods={metric.periods || {}} onChange={onPeriodChange} />
            </div>
            {serverError && <div className={css.errorText}>{serverError}</div>}
            <div className={css.formButtons}>
                <Button type="flat" text="STATIC.BUTTONS.CANCEL" disabled={saving} onClick={onCancel} />
                <Button type="primary" text="STATIC.BUTTONS.SAVE" showSpinner={saving} onClick={onSubmit} />
            </div>
        </div>
    );
}

CustomMetricForm.propTypes = {
    metric: PropTypes.objectOf(PropTypes.any).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    saving: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(CustomMetricForm);
